import { useEffect, useRef, useState } from 'react';
export const usePullToRefresh = ({ onRefresh, threshold = 80, enabled = true }) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startYRef = useRef(null);
    const pullDistanceRef = useRef(0);
    const refreshingRef = useRef(false);
    const thresholdHapticFiredRef = useRef(false);
    const onRefreshRef = useRef(onRefresh);
    onRefreshRef.current = onRefresh;
    useEffect(() => {
        if (!enabled)
            return;
        const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
        if (!isCoarsePointer)
            return;
        const setPull = (d) => {
            pullDistanceRef.current = d;
            setPullDistance(d);
        };
        const onTouchStart = (e) => {
            if (window.scrollY > 0 || refreshingRef.current)
                return;
            // An open overlay (e.g. the profile settings popover) locks body scroll -
            // don't hijack touches meant for its own scrollable content.
            if (document.body.style.overflow === 'hidden')
                return;
            startYRef.current = e.touches[0].clientY;
            thresholdHapticFiredRef.current = false;
        };
        const onTouchMove = (e) => {
            if (startYRef.current === null)
                return;
            if (window.scrollY > 0) {
                startYRef.current = null;
                setPull(0);
                return;
            }
            const delta = e.touches[0].clientY - startYRef.current;
            if (delta <= 0) {
                if (pullDistanceRef.current !== 0)
                    setPull(0);
                return;
            }
            // Damp past threshold so it feels rubbery, not infinite.
            const damped = delta > threshold ? threshold + (delta - threshold) * 0.4 : delta;
            setPull(damped);
            if (damped >= threshold && !thresholdHapticFiredRef.current) {
                thresholdHapticFiredRef.current = true;
                navigator.vibrate?.(8);
            }
            // Suppress Safari's native pull-to-refresh-and-reload while we drive our own.
            if (e.cancelable)
                e.preventDefault();
        };
        const onTouchEnd = async () => {
            if (startYRef.current === null)
                return;
            const wasOver = pullDistanceRef.current >= threshold;
            startYRef.current = null;
            if (wasOver && !refreshingRef.current) {
                navigator.vibrate?.(30);
                refreshingRef.current = true;
                setRefreshing(true);
                setPull(threshold);
                try {
                    await onRefreshRef.current();
                }
                finally {
                    refreshingRef.current = false;
                    setRefreshing(false);
                    setPull(0);
                }
            }
            else {
                setPull(0);
            }
        };
        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
        document.addEventListener('touchcancel', onTouchEnd);
        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            document.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [enabled, threshold]);
    return { pullDistance, refreshing };
};
